import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	Renderer2,
	signal,
	TemplateRef,
	untracked,
	viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	AnnotationLayerRenderedEvent,
	IPDFViewerApplication,
	LinkTarget,
	NgxExtendedPdfViewerComponent,
	NgxExtendedPdfViewerModule,
	NgxExtendedPdfViewerService,
	pdfDefaultOptions,
	PdfLoadedEvent,
	PDFNotificationService,
	ProgressBarEvent,
} from 'ngx-extended-pdf-viewer';

import { FileType } from '@reach/interfaces/files/file-type.enum';
import { BboxTypeEnum } from '@reach/interfaces/nodes/bbox-type.enum';
import { NodeType } from '@reach/interfaces/nodes/node-type.enum';

import { FullscreenService } from '~ng-common-lib/cdk/fullscreen.service';
import { injectSrcConverter } from '~ng-common-lib/cdk/img';
import { ScreenService, ScreenSize } from '~ng-common-lib/cdk/screen.service';
import { ClientNode } from '~ng-common-lib/nodes/types/node';
import { PdfPreviewService } from '~ng-common-lib/previews/services/pdf-preview.service';
import { injectExclusivePdfPreview } from '~ng-common-lib/previews/tokens/pdf-preview';
import { injectPreviewConfiguration } from '~ng-common-lib/previews/tokens/preview-config';
import { SourcePreviewByNodeSource } from '~ng-common-lib/source-preview/types/source-preview-input';
import { ButtonsModule } from '~ng-common-lib/ui/buttons';
import { TenantMarkIconDirective } from '~ng-common-lib/ui/icons/tenant-mark-icon.directive';
import { InputValidatorDirective } from '~ng-common-lib/ui/inputs/input-validator.directive';
import { TooltipsModule } from '~ng-common-lib/ui/tooltips';

import { ChunkTranslatorComponent } from '../translator/translator.component';

enum PDF_ZOOM_LEVEL {
	CUSTOM = 'CUSTOM',
	FIXED_AUTO = 'FIXED_AUTO',
	FIXED_PAGE_ACTUAL = 'FIXED_PAGE_ACTUAL',
	FIXED_PAGE_FIT = 'FIXED_PAGE_FIT',
	FIXED_PAGE_WIDTH = 'FIXED_PAGE_WIDTH',
	'PERCENTAGE_50' = 'PERCENTAGE_50',
	'PERCENTAGE_75' = 'PERCENTAGE_75',
	'PERCENTAGE_100' = 'PERCENTAGE_100',
	'PERCENTAGE_125' = 'PERCENTAGE_125',
	'PERCENTAGE_150' = 'PERCENTAGE_150',
	'PERCENTAGE_200' = 'PERCENTAGE_200',
	'PERCENTAGE_300' = 'PERCENTAGE_300',
	'PERCENTAGE_400' = 'PERCENTAGE_400',
}

const ZOOM_LEVELS_CONFIG: Record<PDF_ZOOM_LEVEL, { label: string; value: string | number }> = {
	[PDF_ZOOM_LEVEL.CUSTOM]: {
		label: 'Custom',
		value: 'custom',
	},
	[PDF_ZOOM_LEVEL.FIXED_AUTO]: {
		label: 'Automatic Zoom',
		value: 'auto',
	},
	[PDF_ZOOM_LEVEL.FIXED_PAGE_ACTUAL]: {
		label: 'Actual Size',
		value: 'page-actual',
	},
	[PDF_ZOOM_LEVEL.FIXED_PAGE_FIT]: {
		label: 'Page Fit',
		value: 'page-fit',
	},
	[PDF_ZOOM_LEVEL.FIXED_PAGE_WIDTH]: {
		label: 'Page Width',
		value: 'page-width',
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_50]: {
		label: '50%',
		value: 50,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_75]: {
		label: '75%',
		value: 75,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_100]: {
		label: '100%',
		value: 100,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_125]: {
		label: '125%',
		value: 125,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_150]: {
		label: '150%',
		value: 150,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_200]: {
		label: '200%',
		value: 200,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_300]: {
		label: '300%',
		value: 300,
	},
	[PDF_ZOOM_LEVEL.PERCENTAGE_400]: {
		label: '400%',
		value: 400,
	},
};

const PDF_ZOOM_KEYS_WITHOUT_CUSTOM = (Object.keys(ZOOM_LEVELS_CONFIG) as PDF_ZOOM_LEVEL[]).filter(
	(key) => {
		return key !== PDF_ZOOM_LEVEL.CUSTOM;
	}
);

const PDF_VIEWER_HANDOFF_MS = 250;
const PDF_LOAD_RETRY_MS = 2500;

@Component({
	selector: 'pdf-preview',
	templateUrl: './pdf-preview.component.html',
	styleUrls: ['./pdf-preview.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		InputValidatorDirective,
		FormsModule,
		ButtonsModule,
		NgxExtendedPdfViewerModule,
		ChunkTranslatorComponent,
		TooltipsModule,
		TenantMarkIconDirective,
	],
	host: {
		class: 'block',
		'[class.bg-190]': '!$hideToolbar()',
		'[class.bg-280]': '$hideToolbar()',
		'[class.p-1]': '$fullscreenEnabled()',
		'[class.pdf-preview--hide-toolbar]': '$hideToolbar()',
		style: '--spacing: 8px;',
	},
})
export class PdfPreviewComponent implements OnDestroy {
	protected readonly DEFAULT_ZOOM_LEVEL = ZOOM_LEVELS_CONFIG.FIXED_PAGE_FIT.value;

	private readonly configuration = injectPreviewConfiguration();
	private readonly srcConverter = injectSrcConverter();
	private readonly notificationService = inject(PDFNotificationService);
	private readonly viewerService = inject(NgxExtendedPdfViewerService);
	private readonly renderer = inject(Renderer2);
	private readonly screenService = inject(ScreenService);
	private readonly fullscreenService = inject(FullscreenService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly pdfPreviewService = inject(PdfPreviewService);
	private readonly isExclusiveViewer = injectExclusivePdfPreview();
	private readonly hostRef = inject(ElementRef<HTMLElement>);

	public readonly $fullscreenEnabled = this.fullscreenService.$fullscreenElement.asReadonly();

	protected readonly $screen = this.screenService.$screen;

	public readonly $smallScreen = computed(() => {
		return this.$screen() === ScreenSize.SMALL;
	});

	public readonly $file = input.required<ClientNode<NodeType.FILE>>({ alias: 'node' });
	public readonly $sources = input<SourcePreviewByNodeSource[]>([], { alias: 'sources' });
	public readonly $highlightOnly = input(false, { alias: 'highlightOnly' });
	public readonly $hideSidebar = input(false, { alias: 'hideSidebar' });
	public readonly $hideToolbar = input(false, { alias: 'hideToolbar' });
	// This is a hack so we can focus on the same source multiple times.
	public readonly $selectedSourceId = input<
		{ id: SourcePreviewByNodeSource['id'] | null; timestamp: number },
		{ id: SourcePreviewByNodeSource['id'] | null }
	>(
		{
			id: null,
			timestamp: Date.now(),
		},
		{
			alias: 'selectedSource',
			transform: (value) => {
				return {
					id: value.id,
					timestamp: Date.now(),
				};
			},
		}
	);

	public readonly $sidebarVisible = signal(true);
	public readonly $authorization = signal(
		(() => {
			const value = this.configuration.pdfAuthorization || true;
			if (typeof value === 'function') {
				return value() || true;
			}
			return value || true;
		})()
	);
	public readonly $customHeaders = signal(this.configuration.customHeaders || {});
	public readonly $page = signal(1);

	protected readonly $annotationTpl = viewChild('annotationTpl', {
		read: TemplateRef<{ source: SourcePreviewByNodeSource; index: number }>,
	});
	protected readonly $pageInput = viewChild<unknown, ElementRef<HTMLInputElement>>('pageInput', {
		read: ElementRef,
	});
	protected readonly $viewer = viewChild(NgxExtendedPdfViewerComponent);

	public $pdfWrapper = viewChild<unknown, ElementRef<HTMLElement>>('pdfWrapper', {
		read: ElementRef<HTMLElement>,
	});

	public readonly $pdfUrl = computed(() => {
		const file = this.$file();
		const reloading = this.$reloading();

		if (reloading) {
			return '';
		}

		const url =
			file.data.fileType === FileType.PDF
				? file.data.fileUrl || ''
				: file.data.previewUrl || '';

		return url ? this.srcConverter(url) : url;
	});

	public readonly $zoomValue = signal(ZOOM_LEVELS_CONFIG.FIXED_PAGE_FIT.value);
	public readonly $maxPage = signal(0);
	public readonly $currentZoomFactor = signal(1);
	public readonly $loading = signal(true);
	public readonly $failed = signal(false);
	public readonly $loadingPercent = signal(0);
	public readonly $reloading = signal(false);
	public readonly $chunkToTranslate = signal<{ nodeId: string; chunkId: string } | null>(null);

	private readonly pdfRenderId = this.pdfPreviewService.generateId(this.destroyRef, {
		exclusive: this.isExclusiveViewer,
	});
	private readonly $viewerReady = signal(false);
	private viewerReadyTimeout: ReturnType<typeof setTimeout> | null = null;
	private loadRetryTimeout: ReturnType<typeof setTimeout> | null = null;

	private readonly $isSelectedViewer = computed(
		() => this.pdfPreviewService.$selectedId() === this.pdfRenderId
	);

	public readonly $renderPdf = computed(() => this.$isSelectedViewer() && this.$viewerReady());

	public readonly $showLoadingOverlay = computed(
		() =>
			this.$isSelectedViewer() && (this.$loading() || !this.$viewerReady()) && !this.$failed()
	);

	public readonly $unsuported = computed(() => {
		const file = this.$file();
		const pdfUrl = this.$pdfUrl();

		return file && !pdfUrl;
	});

	public readonly $allZooms = computed(() => {
		const base = PDF_ZOOM_KEYS_WITHOUT_CUSTOM.reduce<
			Array<{ key: PDF_ZOOM_LEVEL; label: string; value: string | number }>
		>((acc, key) => {
			const values = ZOOM_LEVELS_CONFIG[key];
			acc.push({
				key,
				...values,
			});
			return acc;
		}, []);

		return base;
	});

	public readonly $zoomLabel = computed(() => {
		const zoomValue = this.$zoomValue();
		const all = this.$allZooms();
		const zoomFactor = this.$currentZoomFactor();

		const existing = all.find((item) => item.value === zoomValue);

		if (!existing) {
			return `${Math.round(zoomFactor * 100)}%`;
		}

		return existing.label;
	});

	public readonly $removeEverythingExceptIntegers = computed(() => {
		const maxPage = this.$maxPage();

		return (value: string | number): number => {
			const clean = `${value || ''}`.replace(/[^0-9]/g, '');
			return Math.max(1, Math.min(maxPage, Number(clean)));
		};
	});

	private readonly annotationsElements = new Map<string, HTMLElement>();
	private lastScrollElementTimestamp = Date.now();

	constructor() {
		pdfDefaultOptions.externalLinkTarget = LinkTarget.BLANK;

		this.scheduleViewerMount();
		this.destroyRef.onDestroy(() => this.clearScheduledWork());

		effect(() => {
			const hideSidebar = this.$hideSidebar();
			untracked(() => {
				if (hideSidebar) {
					this.$sidebarVisible.set(false);
				}
			});
		});

		effect(() => {
			const sources = this.$sources();
			untracked(() => {
				this.purgeAnnotations();
				// Sources often arrive after pages already rendered; re-paint without
				// resetting the current page or forcing a loading state.
				this.paintAnnotationsForRenderedPages(sources);
			});
		});

		effect(() => {
			const selectedSourceId = this.$selectedSourceId();
			const sources = this.$sources();

			untracked(() => {
				this.syncHighlightVisibility();

				const selectedSource = sources.find((source) => source.id === selectedSourceId.id);
				if (
					selectedSource &&
					selectedSourceId.timestamp !== this.lastScrollElementTimestamp
				) {
					this.lastScrollElementTimestamp = selectedSourceId.timestamp;
					this.goToSource(selectedSource);
				}
			});
		});
	}

	private scheduleViewerMount(): void {
		const mount = (): void => {
			this.pdfPreviewService.selectId(this.pdfRenderId);
			this.$viewerReady.set(true);
			this.scheduleLoadRetry();
		};

		if (this.pdfPreviewService.$ids().length > 1) {
			this.viewerReadyTimeout = setTimeout(mount, PDF_VIEWER_HANDOFF_MS);
			return;
		}

		mount();
	}

	private scheduleLoadRetry(): void {
		this.loadRetryTimeout = setTimeout(() => {
			if (this.$loading() && this.$renderPdf()) {
				this.retry();
			}
		}, PDF_LOAD_RETRY_MS);
	}

	private clearScheduledWork(): void {
		clearTimeout(this.viewerReadyTimeout ?? undefined);
		clearTimeout(this.loadRetryTimeout ?? undefined);
	}

	ngOnDestroy(): void {
		this.purgeAnnotations();
	}

	public retry(): void {
		this.$reloading.set(true);

		requestAnimationFrame(() => {
			this.$reloading.set(false);
		});
	}

	public toggleSidebar(): void {
		this.$sidebarVisible.update((prev) => {
			return !prev;
		});
	}

	public resetZoom(): void {
		this.$zoomValue.set(ZOOM_LEVELS_CONFIG.FIXED_PAGE_FIT.value);
	}

	public requestFullscreen(): void {
		const wrapper = this.$pdfWrapper()?.nativeElement;
		if (wrapper) {
			this.fullscreenService.toggle(wrapper, this.destroyRef);
		}
	}

	protected onAnnotationLayerRendered(event: AnnotationLayerRenderedEvent): void {
		const pageNum = Number(event.pageNumber);
		const pageSources = this.$sources().filter(
			(source) => Number(source.data?.boundingBox.page) === pageNum
		);

		for (const source of pageSources) {
			this.annotationsElements.get(source.id)?.remove();
			const rect = this.addSvgToPageAndCoordinates(source);
			if (rect) {
				this.annotationsElements.set(source.id, rect);
			}
		}
	}

	protected onProgress(event: ProgressBarEvent): void {
		this.$loading.set(event.percent < 100);
		this.$loadingPercent.set(event.percent);
	}

	protected onPdfStartsLoading(): void {
		this.purgeAnnotations();
		this.$loading.set(true);
		this.$failed.set(false);
		this.$loadingPercent.set(0);
		this.$page.set(1);
		this.$maxPage.set(0);
	}

	protected onPdfFailed(): void {
		this.$loading.set(false);
		this.$failed.set(true);
	}

	protected onPdfLoaded(event: PdfLoadedEvent): void {
		this.$loading.set(false);
		this.$failed.set(false);
		this.$loadingPercent.set(100);
		this.$maxPage.set(event.pagesCount);
		const selectedSourceId = this.$selectedSourceId();
		const sources = this.$sources();

		const selectedSource = sources.find((source) => source.id === selectedSourceId.id);

		if (selectedSource) {
			this.goToSource(selectedSource);
		}
	}

	protected zoomOut(): void {
		this.getPdfViewerApplication()?.eventBus.dispatch('zoomout');
		// TODO: Implement using zoomFactor if needed;
	}

	protected zoomIn(): void {
		this.getPdfViewerApplication()?.eventBus.dispatch('zoomin');
		// TODO: Implement using zoomFactor if needed;
	}

	protected rotateRight(): void {
		this.getPdfViewerApplication()?.eventBus.dispatch('rotatecw');
	}

	protected increasePage(): void {
		const max = this.$maxPage();

		if (max > 0) {
			this.$page.update((prev) => {
				return Math.min(prev + 1, max);
			});
		}
	}

	protected decreasePage(): void {
		this.$page.update((prev) => {
			return Math.max(prev - 1, 1);
		});
	}

	protected translateChunk(source: SourcePreviewByNodeSource): void {
		this.$chunkToTranslate.set({
			nodeId: this.$file().id,
			chunkId: source.id,
		});
	}

	protected closeTranslation(): void {
		this.$chunkToTranslate.set(null);
	}

	protected getAnnotationLayout(box: SourcePreviewByNodeSource['data']['boundingBox']): {
		top: number;
		left: number;
		width: number;
		height: number;
		normalized: boolean;
	} {
		const top = Math.min(box.top, box.bottom);
		const left = Math.min(box.left, box.right);
		const width = Math.abs(box.right - box.left);
		const height = Math.abs(box.bottom - box.top);
		const normalized = [box.top, box.right, box.bottom, box.left].every(
			(value) => Number.isFinite(value) && value >= 0 && value <= 1
		);

		return { top, left, width, height, normalized };
	}

	private addSvgToPageAndCoordinates(source: SourcePreviewByNodeSource): HTMLElement | null {
		const layout = this.getAnnotationLayout(source.data.boundingBox);
		const hasValidBBox =
			source.data.bboxType === BboxTypeEnum.POINT &&
			Number.isFinite(layout.left) &&
			Number.isFinite(layout.top) &&
			layout.width > 0 &&
			layout.height > 0;

		// Non-supported bbox types are skipped in source-preview, but highlight-only mode
		// still needs the bbox rectangle when coordinates are available.
		if (this.$highlightOnly() && !hasValidBBox) {
			return null;
		}

		if (!hasValidBBox) {
			return null;
		}

		const page = Number(source.data.boundingBox.page);
		if (!Number.isFinite(page) || page < 1) {
			return null;
		}

		const pageElement = this.hostRef.nativeElement.querySelector(
			`.page[data-page-number="${page}"]`
		);
		if (!pageElement) {
			return null;
		}

		let annotationLayer = pageElement.querySelector(
			'.annotationEditorLayer'
		) as HTMLElement | null;
		if (!annotationLayer) {
			annotationLayer = this.renderer.createElement('div');
			this.renderer.addClass(annotationLayer, 'annotationEditorLayer');
			this.renderer.setStyle(annotationLayer, 'position', 'absolute');
			this.renderer.setStyle(annotationLayer, 'inset', '0');
			this.renderer.setStyle(annotationLayer, 'pointer-events', 'none');
			this.renderer.appendChild(pageElement, annotationLayer);
		}

		this.renderer.removeAttribute(annotationLayer, 'hidden');

		const view = this.$annotationTpl()?.createEmbeddedView({ source });
		view?.detectChanges();
		const rect = (view?.rootNodes[0] as HTMLElement) ?? null;
		if (!rect) {
			return null;
		}

		this.renderer.appendChild(annotationLayer, rect);
		this.syncHighlightVisibilityForElement(rect, source.id);

		const selected = this.$selectedSourceId();
		if (selected.id === source.id && this.lastScrollElementTimestamp !== selected.timestamp) {
			this.lastScrollElementTimestamp = selected.timestamp;
			requestAnimationFrame(() => {
				rect.scrollIntoView({ behavior: 'smooth', block: 'center' });
			});
		}

		return rect;
	}

	private paintAnnotationsForRenderedPages(sources: SourcePreviewByNodeSource[]): void {
		if (!sources.length) {
			return;
		}

		const sourcesByPage = new Map<number, SourcePreviewByNodeSource[]>();
		for (const source of sources) {
			const pageNum = Number(source.data?.boundingBox.page);
			if (!Number.isFinite(pageNum)) {
				continue;
			}
			const pageSources = sourcesByPage.get(pageNum);
			if (pageSources) {
				pageSources.push(source);
			} else {
				sourcesByPage.set(pageNum, [source]);
			}
		}

		const pages = this.hostRef.nativeElement.querySelectorAll('.page[data-page-number]');
		pages.forEach((pageElement: Element) => {
			const pageNum = Number(pageElement.getAttribute('data-page-number'));
			const pageSources = sourcesByPage.get(pageNum);
			if (!pageSources?.length) {
				return;
			}

			for (const source of pageSources) {
				this.annotationsElements.get(source.id)?.remove();
				const rect = this.addSvgToPageAndCoordinates(source);
				if (rect) {
					this.annotationsElements.set(source.id, rect);
				}
			}
		});
	}

	private syncHighlightVisibility(): void {
		if (!this.$highlightOnly()) {
			return;
		}

		const selectedId = this.$selectedSourceId().id;
		this.annotationsElements.forEach((element, id) => {
			this.syncHighlightVisibilityForElement(element, id, selectedId);
		});
	}

	private syncHighlightVisibilityForElement(
		element: HTMLElement,
		sourceId: string,
		selectedId: string | null = this.$selectedSourceId().id
	): void {
		if (!this.$highlightOnly()) {
			return;
		}

		const highlight = element.querySelector('.chunk-highlight');
		if (!highlight) {
			return;
		}

		if (selectedId && selectedId === sourceId) {
			this.renderer.removeClass(highlight, 'invisible');
		} else {
			this.renderer.addClass(highlight, 'invisible');
		}
	}

	private purgeAnnotations(): void {
		this.annotationsElements.forEach((element) => {
			element.remove();
		});
		this.annotationsElements.clear();
	}

	private goToSource(source: SourcePreviewByNodeSource): void {
		const page = Number(source.data.boundingBox.page);
		if (!Number.isFinite(page) || page < 1) {
			return;
		}

		this.$page.set(page);
		this.viewerService.scrollPageIntoView(page);

		requestAnimationFrame(() => {
			let rect = this.annotationsElements.get(source.id);
			if (!rect) {
				rect = this.addSvgToPageAndCoordinates(source) ?? undefined;
				if (rect) {
					this.annotationsElements.set(source.id, rect);
				}
			}
			rect?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		});
	}

	private getPdfViewerApplication(): IPDFViewerApplication | null {
		return this.notificationService.onPDFJSInitSignal() ?? null;
	}
}
